import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, KeyRound, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Subscription lapsed modal
  const [showLapsedModal, setShowLapsedModal] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter username, 2 = enter code & new password
  const [forgotLoading, setForgotLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(''); // For demo purposes

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      toast.success('Login successful');
      navigate('/dashboard');
    } else {
      // Check if subscription lapsed
      if (result.error === 'subscription_lapsed' || result.subscriptionLapsed) {
        setShowLapsedModal(true);
      } else {
        toast.error(result.error || 'Login failed');
      }
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotUsername.trim()) {
      toast.error('Please enter your username');
      return;
    }

    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        username: forgotUsername
      });
      
      // Store the generated code for demo purposes
      if (response.data.reset_code) {
        setGeneratedCode(response.data.reset_code);
      }
      
      toast.success('Reset code generated! Contact your administrator or use the code shown.');
      setForgotStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request password reset');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode.trim()) {
      toast.error('Please enter the reset code');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        username: forgotUsername,
        reset_code: resetCode,
        new_password: newPassword
      });
      
      toast.success('Password reset successfully! You can now login.');
      closeForgotPassword();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotUsername('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setGeneratedCode('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
         style={{
           backgroundImage: 'url(https://images.unsplash.com/photo-1629909613654-28e377c37b09?q=80&w=2068&auto=format&fit=crop)',
           backgroundSize: 'cover',
           backgroundPosition: 'center'
         }}>
      <div className="absolute inset-0 bg-slate-50/80 backdrop-blur-sm"></div>
      
      <div className="relative w-full max-w-md">
        <div className="glass-panel rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <button onClick={() => navigate('/')} data-testid="login-logo-link" type="button">
              <img 
                src="https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png" 
                alt="DentalPulse"
                className="h-20 w-auto mx-auto mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                data-testid="login-logo"
              />
            </button>
            <p className="text-slate-600 text-sm">Real-Time Dental Office Communication</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div data-testid="login-form">
              <Label htmlFor="username" className="text-slate-700 font-medium">Username</Label>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 bg-white/50 border-white/30 focus:bg-white/70"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white/50 border-white/30 focus:bg-white/70"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full bg-dp-600 hover:bg-dp-700 text-white font-semibold py-6 rounded-xl transition-all hover:shadow-lg"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-dp-500 hover:text-dp-600 text-sm font-medium hover:underline"
              data-testid="forgot-password-link"
            >
              I forgot my password
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Lapsed Modal */}
      <Dialog open={showLapsedModal} onOpenChange={setShowLapsedModal}>
        <DialogContent className="sm:max-w-md" data-testid="subscription-lapsed-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Subscription Expired</DialogTitle>
            </div>
          </DialogHeader>
          <DialogDescription className="text-base py-4">
            The account subscription has lapsed. Contact your administrator to regain access to Dental Pulse.
          </DialogDescription>
          <DialogFooter>
            <Button 
              onClick={() => setShowLapsedModal(false)}
              className="w-full bg-slate-800 hover:bg-slate-900"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => !open && closeForgotPassword()}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-dp-100 flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-dp-600" />
              </div>
              <DialogTitle className="text-xl">
                {forgotStep === 1 ? 'Forgot Password' : 'Reset Password'}
              </DialogTitle>
            </div>
          </DialogHeader>

          {forgotStep === 1 ? (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Enter your username to receive a password reset code. You will need to contact your administrator to get the code.
              </p>
              <div>
                <Label htmlFor="forgot-username">Username</Label>
                <Input
                  id="forgot-username"
                  data-testid="forgot-username-input"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="mt-2"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {generatedCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-800">Demo Mode: Your reset code is:</p>
                  <p className="font-mono text-lg text-amber-900 mt-1">{generatedCode}</p>
                  <p className="text-amber-700 text-xs mt-1">In production, this would be sent to your administrator.</p>
                </div>
              )}
              <div>
                <Label htmlFor="reset-code">Reset Code</Label>
                <Input
                  id="reset-code"
                  data-testid="reset-code-input"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="mt-2 font-mono text-center text-lg tracking-widest"
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="mt-2"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {forgotStep === 2 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotStep(1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            <Button
              type="button"
              onClick={forgotStep === 1 ? handleForgotPassword : handleResetPassword}
              disabled={forgotLoading}
              className="flex-1 bg-dp-600 hover:bg-dp-700"
            >
              {forgotLoading ? 'Processing...' : (forgotStep === 1 ? 'Get Reset Code' : 'Reset Password')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
